import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import { setTutorialCompleted as setTutorialCompletedAPI, setPreferredCommodities } from '../utils/api';

const TutorialContext = createContext();

/**
 * Persistence model (unchanged from the pre-redesign tutorial):
 *
 *   Firestore `userDetails.tutorial_completed`  → durable source of truth,
 *                                                 follows the user across
 *                                                 browsers and devices.
 *   localStorage `tutorial_completed`           → local cache of the above, so
 *                                                 the first paint after login
 *                                                 doesn't wait on the network.
 *   sessionStorage `tutorial_hidden_session`    → "skipped for now", cleared
 *                                                 when the tab closes.
 *
 * Either persistent source saying "done" hides the tour. When Firestore says
 * done but the cache doesn't know yet, the cache is warmed — that write is why
 * hand-editing localStorage alone can't force the tour back; use
 * `resetTutorial()` (or `__tutorial.reset()` in dev), which clears all three.
 */
export const TUTORIAL_COMPLETED_KEY = 'tutorial_completed';
export const TUTORIAL_SNOOZED_KEY = 'tutorial_hidden_session';

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

export const TutorialProvider = ({ children }) => {
  const { userDetails, isAuthenticated } = useUser();
  const [isTutorialVisible, setIsTutorialVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [tutorialCompleted, setTutorialCompleted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Must match the number of live entries in TUTORIAL_STEPS (TutorialModal.jsx).
  const totalSteps = 5;

  // Decide once per session, on the first render where userDetails is available.
  // The isInitialized guard matters because userDetails is a react-query result
  // that refetches in the background — without it, a refetch could reopen the
  // tour after the user had already skipped it.
  useEffect(() => {
    if (!isAuthenticated || !userDetails || isInitialized) return;

    const completedRemotely = userDetails.tutorial_completed === true;
    const completedLocally = localStorage.getItem(TUTORIAL_COMPLETED_KEY) === 'true';

    if (completedRemotely || completedLocally) {
      setTutorialCompleted(true);
      // Warm the local cache from the durable flag.
      if (completedRemotely && !completedLocally) {
        localStorage.setItem(TUTORIAL_COMPLETED_KEY, 'true');
      }
    } else if (sessionStorage.getItem(TUTORIAL_SNOOZED_KEY) !== 'true') {
      setCurrentStep(1);
      setIsTutorialVisible(true);
    }

    setIsInitialized(true);
  }, [userDetails, isAuthenticated, isInitialized]);

  // Replay the tour on demand (e.g. the FAQ "view tutorial again" link).
  const startTutorial = useCallback(() => {
    setCurrentStep(1);
    setIsTutorialVisible(true);
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  }, [totalSteps]);

  const previousStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  /**
   * "Skip tour" — hides for this session only, without marking the tour done.
   * It comes back on the next login. Nothing is persisted beyond sessionStorage.
   */
  const skipTutorial = useCallback(() => {
    setIsTutorialVisible(false);
    sessionStorage.setItem(TUTORIAL_SNOOZED_KEY, 'true');
  }, []);

  /**
   * "Finish" — marks the tour done for good, in both the cache and Firestore.
   * The remote write is best-effort: if it fails the local cache still keeps
   * the tour closed on this browser.
   */
  const completeTutorial = useCallback(async (selectedCommodities = []) => {
    setIsTutorialVisible(false);
    setTutorialCompleted(true);
    localStorage.setItem(TUTORIAL_COMPLETED_KEY, 'true');

    try {
      await setTutorialCompletedAPI(true);
    } catch (error) {
      console.error('Failed to persist tutorial completion to backend:', error);
    }

    // Guarded with isArray because button handlers may pass a click event.
    if (Array.isArray(selectedCommodities) && selectedCommodities.length > 0) {
      try {
        await setPreferredCommodities(selectedCommodities);
      } catch (error) {
        console.error('Failed to save preferred commodities:', error);
      }
    }
  }, []);

  /**
   * Clear every flag and reopen the tour. Unlike the old reset, this also
   * clears the Firestore flag — otherwise the next reload would read
   * `tutorial_completed: true` from the server and immediately re-hide it.
   */
  const resetTutorial = useCallback(async () => {
    localStorage.removeItem(TUTORIAL_COMPLETED_KEY);
    sessionStorage.removeItem(TUTORIAL_SNOOZED_KEY);
    setCurrentStep(1);
    setTutorialCompleted(false);
    setIsTutorialVisible(true);

    try {
      await setTutorialCompletedAPI(false);
    } catch (error) {
      console.error('Failed to clear tutorial completion on backend:', error);
    }
  }, []);

  // Dev console handle: `__tutorial.reset()` reopens the tour on the spot,
  // `__tutorial.state()` shows which of the three flags is hiding it.
  useEffect(() => {
    if (!import.meta.env.DEV) return undefined;
    window.__tutorial = {
      reset: resetTutorial,
      show: startTutorial,
      state: () => ({
        completedRemotely: userDetails?.tutorial_completed === true,
        completedLocally: localStorage.getItem(TUTORIAL_COMPLETED_KEY) === 'true',
        snoozedThisSession: sessionStorage.getItem(TUTORIAL_SNOOZED_KEY) === 'true',
      }),
    };
    return () => {
      delete window.__tutorial;
    };
  }, [resetTutorial, startTutorial, userDetails]);

  const value = {
    isTutorialVisible,
    currentStep,
    totalSteps,
    tutorialCompleted,
    isInitialized,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completeTutorial,
    resetTutorial,
    setIsTutorialVisible,
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
